import { useState, useCallback, useMemo, useEffect } from "react";

export type SelectionState =
  | "none" // no transactions are selected
  | "partialPage" // some transactions on the page are selected
  | "currentPage" // all transactions on the current page are selected
  | "allPages"; // all transactions across all pages are selected

export type SelectedTxProps = {
  selectedCount: number | undefined;
  totalCount: number | undefined;

  selectionState: SelectionState;

  // helper for determining if a transaction is selected
  isSelected: (id: string) => boolean;

  // select specific transaction ids on the current page
  onSelect: (id: string) => void;
  selectedIds: string[]; // empty if allPagesSelected is true

  // select all on the current page only (not across all pages)
  selectCurrentPage: () => void;

  // select all pages
  selectAllPages: () => void;

  clearAll: () => void;
  clearCurrentPage: () => void;
};

export type SelectedTxInputProps = {
  // total count of transactions across all pages (matching the current filters)
  availableTransactionCount: number | undefined;

  // only the current page of group ids
  availableGroupIds: string[] | undefined;
};

export function useSelectedList({
  availableTransactionCount,
  availableGroupIds,
}: SelectedTxInputProps): SelectedTxProps {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [allPagesSelected, setAllPagesSelectedState] = useState(false);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // whenever the user changes the filter or paginates, we clear the ids
  // which are no longer on the current page
  useEffect(() => {
    if (availableGroupIds === undefined) {
      return;
    }

    const availableGroupIdSet = new Set(availableGroupIds);

    setSelectedIds((selected) =>
      selected.filter((selectedId) => availableGroupIdSet.has(selectedId)),
    );
  }, [availableGroupIds]);

  const selectionState = useMemo<SelectionState>(() => {
    if (allPagesSelected) {
      return "allPages";
    }

    if (
      selectedIds.length === 0 ||
      availableGroupIds === undefined ||
      availableGroupIds.length === 0
    ) {
      return "none";
    }

    if (availableGroupIds.every((groupId) => selectedIdSet.has(groupId))) {
      return "currentPage";
    }

    return "partialPage";
  }, [allPagesSelected, selectedIds.length, availableGroupIds, selectedIdSet]);

  const selectAllPages = useCallback(() => {
    setSelectedIds([]);
    setAllPagesSelectedState(true);
  }, []);

  const selectCurrentPage = useCallback(() => {
    if (availableGroupIds === undefined) {
      return;
    }

    setAllPagesSelectedState(false);
    setSelectedIds(availableGroupIds);
  }, [availableGroupIds]);

  // select a single transaction
  const onSelect = useCallback(
    (groupId: string) => {
      if (availableGroupIds === undefined) {
        return;
      }

      if (allPagesSelected) {
        setAllPagesSelectedState(false);
        setSelectedIds(
          availableGroupIds.filter(
            (availableGroupId) => availableGroupId !== groupId,
          ),
        );
        return;
      }

      setSelectedIds((selected) => {
        if (selected.includes(groupId)) {
          // deselect the transaction
          return selected.filter((selectedId) => selectedId !== groupId);
        }
        // select the transaction
        return [...selected, groupId];
      });
    },
    [allPagesSelected, availableGroupIds],
  );

  const clearAll = useCallback(() => {
    setSelectedIds([]);
    setAllPagesSelectedState(false);
  }, []);

  const clearCurrentPage = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const isSelected = useCallback(
    (groupId: string) => allPagesSelected || selectedIdSet.has(groupId),
    [allPagesSelected, selectedIdSet],
  );

  const selectedCount =
    allPagesSelected ? availableTransactionCount : selectedIds.length;

  const totalCount = availableTransactionCount;

  return {
    selectionState,
    onSelect,
    selectedIds,
    isSelected,
    clearAll,
    clearCurrentPage,
    selectCurrentPage,
    selectAllPages,
    selectedCount,
    totalCount,
  };
}
